/**
 * RemoteActions — acciones remotas sobre un CI (Fase 3).
 *
 * Dos capas, igual que en el backend:
 *  · acciones de la lista (diagnóstico y operaciones comunes, parámetros
 *    validados) para cualquiera que pueda editar;
 *  · modo avanzado (comando libre) solo si el usuario es administrador.
 *
 * El resultado se refresca solo mientras hay algo en marcha: nadie tiene que
 * pulsar «actualizar» para ver si terminó.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlayIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CommandLineIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import './RemoteActions.css';

const STATUS_TONE = {
  pending: 'info',
  received: 'info',
  running: 'warn',
  success: 'ok',
  failed: 'bad',
  cancelled: 'muted',
  timeout: 'bad',
};

const STATUS_LABEL_KEY = {
  pending: 'cmd.status.pending',
  received: 'cmd.status.received',
  running: 'cmd.status.running',
  success: 'cmd.status.success',
  failed: 'cmd.status.failed',
  cancelled: 'cmd.status.cancelled',
  timeout: 'cmd.status.timeout',
};

const ACTIVE = ('pending received running').split(' ');

export default function RemoteActions({ companyId, ciType, ciId }) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState({ actions: [], advanced: false, can_run: false });
  const [actionKey, setActionKey] = useState('');
  const [params, setParams] = useState({});
  const [reason, setReason] = useState('');
  const [advancedCommand, setAdvancedCommand] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [openOutput, setOpenOutput] = useState({});
  const [confirming, setConfirming] = useState(false);
  // El formulario de acciones arranca plegado: en la pestaña Agente lo que
  // manda es lo que está pasando ahora, no un formulario de diez campos.
  const [formOpen, setFormOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const timerRef = useRef(null);

  const loadHistory = useCallback(async () => {
    if (!companyId || !ciId) return [];
    const resp = await api.getCiCommands(companyId, { ci_type: ciType, ci_id: ciId });
    const rows = (resp && resp.items) || [];
    setItems(rows);
    return rows;
  }, [companyId, ciType, ciId]);

  const load = useCallback(async () => {
    if (!companyId || !ciId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [cat] = await Promise.all([
        api.getCiCommandsCatalog(companyId, { ci_type: ciType, ci_id: ciId }),
        loadHistory(),
      ]);
      setCatalog(cat || { actions: [], advanced: false, can_run: false });
    } catch (err) {
      setError(err.message || t('cmd.err.load'));
    } finally {
      setLoading(false);
    }
  }, [companyId, ciType, ciId, loadHistory, t]);

  useEffect(() => { load(); }, [load]);

  // Refresco automático mientras algo está en marcha.
  useEffect(() => {
    const pending = items.some((item) => ACTIVE.includes(item.status));
    if (!pending) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }
    if (timerRef.current) return undefined;
    timerRef.current = setInterval(() => {
      loadHistory().catch(() => {});
    }, 5000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [items, loadHistory]);

  const action = useMemo(
    () => (catalog.actions || []).find((item) => item.action === actionKey) || null,
    [catalog.actions, actionKey]
  );

  const run = async () => {
    if (!actionKey) {
      setError(t('cmd.err.action'));
      return;
    }
    setRunning(true);
    setError('');
    setNotice('');
    try {
      const payload = { ci_type: ciType, ci_id: ciId, action: actionKey, reason: reason.trim() };
      if (actionKey === 'advanced') payload.command = advancedCommand;
      else payload.params = params;
      await api.runCiCommand(payload, companyId);
      setNotice(t('cmd.queued'));
      await loadHistory();
      setParams({});
      setReason('');
      if (actionKey === 'advanced') {
        setAdvancedCommand('');
        setActionKey('');
      }
    } catch (err) {
      setError(err.message || t('cmd.err.run'));
    } finally {
      setRunning(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.cancelCiCommand(id, companyId);
      await loadHistory();
    } catch (err) {
      setError(err.message || t('cmd.err.cancel'));
    }
  };

  if (loading) {
    return <div className="rmx-card"><p className="rmx-hint">{t('cmd.loading')}</p></div>;
  }

  const canRun = Boolean(catalog.can_run);
  const activeItems = items.filter((item) => ACTIVE.includes(item.status));
  // Plegado por defecto: solo se ve lo que está en curso. El historial no se
  // pinta hasta que el usuario lo pide, para no comerse el alto de la pestaña.
  const visibleItems = activeItems.length > 0
    ? activeItems
    : (showAll ? items : []);

  return (
    <div className="rmx-card">
      <div className="rmx-head">
        <span className="rmx-icon"><CommandLineIcon /></span>
        <div>
          <h3>{t('cmd.title')}</h3>
          <p className="rmx-hint">{t('cmd.subtitle')}</p>
        </div>
        <button type="button" className="rmx-btn" onClick={load}>
          <ArrowPathIcon />
          {t('cmd.refresh')}
        </button>
        {canRun && (
          <button
            type="button"
            className="rmx-btn"
            onClick={() => setFormOpen((open) => !open)}
            aria-expanded={formOpen}
          >
            {formOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
            {formOpen ? t('cmd.cancel', 'Cerrar') : t('cmd.new', 'Nueva acción')}
          </button>
        )}
      </div>

      {!canRun && <p className="rmx-hint">{t('cmd.noPermission')}</p>}

      {error && (
        <div className="rmx-alert">
          <ExclamationTriangleIcon />
          <span>{error}</span>
        </div>
      )}
      {notice && !error && <div className="rmx-notice">{notice}</div>}

      {canRun && formOpen && (
        <div className="rmx-form">
          <label htmlFor="rmx-action">{t('cmd.pick')}</label>
          <select
            id="rmx-action"
            value={actionKey}
            onChange={(event) => {
              setActionKey(event.target.value);
              setParams({});
              setConfirming(false);
            }}
          >
            <option value="">{t('cmd.pickPlaceholder')}</option>
            {(catalog.actions || []).map((item) => (
              <option key={item.action} value={item.action}>
                {t(`cmd.action.${item.action}`, { defaultValue: item.label })}
                {item.risk === 'high' ? ` · ${t('cmd.riskHigh')}` : ''}
              </option>
            ))}
            {catalog.advanced && <option value="advanced">{t('cmd.action.advanced')}</option>}
          </select>

          {action && action.params.map((param) => (
            <div key={param.name} className="rmx-field">
              <label htmlFor={`rmx-p-${param.name}`}>{param.label}</label>
              <input
                id={`rmx-p-${param.name}`}
                value={params[param.name] || ''}
                onChange={(event) => setParams({ ...params, [param.name]: event.target.value })}
                placeholder={t('cmd.paramPlaceholder')}
                autoComplete="off"
              />
            </div>
          ))}

          {actionKey === 'advanced' && (
            <div className="rmx-field rmx-field--wide">
              <label htmlFor="rmx-cmd">{t('cmd.advancedLabel')}</label>
              <input
                id="rmx-cmd"
                value={advancedCommand}
                onChange={(event) => setAdvancedCommand(event.target.value)}
                placeholder="ipconfig /all"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}

          <div className="rmx-field rmx-field--wide">
            <label htmlFor="rmx-reason">{t('cmd.reason')}</label>
            <input
              id="rmx-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('cmd.reasonPlaceholder')}
              autoComplete="off"
            />
          </div>

          <div className="rmx-actions">
            {action && action.risk !== 'low' && (
              <span className={`rmx-risk is-${action.risk}`}>{t(`cmd.risk.${action.risk}`)}</span>
            )}
            <button
              type="button"
              className="rmx-btn rmx-btn--primary"
              onClick={() => {
                // Riesgo medio/alto: el primer clic no ejecuta, solo confirma.
                const risky = actionKey === 'advanced' || (action && action.risk !== 'low');
                if (risky && !confirming) {
                  setConfirming(true);
                  return;
                }
                setConfirming(false);
                run();
              }}
              disabled={running || !actionKey}
            >
              <PlayIcon />
              {running
                ? t('cmd.sending')
                : confirming
                  ? t('cmd.confirm')
                  : t('cmd.run')}
            </button>
          </div>
        </div>
      )}

      <div className="rmx-history">
        <h4>
          {activeItems.length > 0 ? t('cmd.inProgress', 'En curso') : t('cmd.history')}
          {activeItems.length > 0 && (
            <span className="rmx-pill is-warn" style={{ marginLeft: 8 }}>{activeItems.length}</span>
          )}
        </h4>
        {items.length === 0 ? (
          <p className="rmx-hint">{t('cmd.noHistory')}</p>
        ) : visibleItems.length === 0 ? (
          <p className="rmx-hint">
            {t(
              'cmd.historyFolded',
              'Sin acciones en curso. El historial está plegado para no quitar sitio.',
            )}
          </p>
        ) : (
          <ul>
            {visibleItems.map((item) => {
              const tone = STATUS_TONE[item.status] || 'muted';
              const hasOutput = Boolean((item.stdout || '').trim() || (item.stderr || '').trim());
              const open = Boolean(openOutput[item.id]);
              return (
                <li key={item.id} className="rmx-run">
                  <button
                    type="button"
                    className="rmx-run-head"
                    onClick={() => hasOutput && setOpenOutput({ ...openOutput, [item.id]: !open })}
                    aria-expanded={open}
                  >
                    {hasOutput ? (open ? <ChevronDownIcon /> : <ChevronRightIcon />) : <span className="rmx-dot" />}
                    <span className="rmx-run-title">
                      {item.action
                        ? t(`cmd.action.${item.action}`, { defaultValue: item.action_label || item.action })
                        : '—'}
                    </span>
                    <span className={`rmx-pill is-${tone}`}>{t(STATUS_LABEL_KEY[item.status] || 'cmd.status.pending')}</span>
                    <span className="rmx-run-meta">
                      {item.created_at ? new Date(item.created_at).toLocaleString('es-ES') : ''}
                      {item.exit_code !== null && item.exit_code !== undefined ? ` · exit ${item.exit_code}` : ''}
                    </span>
                  </button>
                  {ACTIVE.includes(item.status) && (
                    <button type="button" className="rmx-cancel" onClick={() => cancel(item.id)}>
                      {t('cmd.cancel')}
                    </button>
                  )}
                  {open && hasOutput && (
                    <pre className="rmx-output">
                      {(item.stdout || '').trim()}
                      {(item.stderr || '').trim() ? `\n\n--- stderr ---\n${item.stderr.trim()}` : ''}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {!showAll && items.length > visibleItems.length && (
          <button type="button" className="rmx-btn" onClick={() => setShowAll(true)}>
            {t('cmd.showAll', 'Ver historial completo')} ({items.length})
          </button>
        )}
        {showAll && (
          <button type="button" className="rmx-btn" onClick={() => setShowAll(false)}>
            {t('cmd.showLess', 'Ocultar historial')}
          </button>
        )}
      </div>
    </div>
  );
}
